import { defaultsDeep, set } from 'lodash-es';

export const CONTENT_TYPE = {
  JSON: 'application/json',
};
const DEFAULT_API_KEY_HEADER = 'apikey';

function applyDefaults(requestOptions: RequestInit) {
  return defaultsDeep(requestOptions, {
    credentials: 'include',
    headers: {
      Accept: CONTENT_TYPE.JSON,
      'X-TRP-Requested-With': 'nitro-auth-service',
    },
    mode: 'cors',
  });
}

interface BuilderOptions {
  apiKey?: string;
  apiKeyHeaderName?: string;
  initialOptions?: RequestInit;
}

/**
 * Adds default options and provides builder capabilities for request options.
 */
class Builder {
  private readonly apiKey: string | undefined;
  private readonly apiKeyHeaderName: string | undefined;
  private readonly options: RequestInit;

  constructor(options: BuilderOptions) {
    this.apiKey = options.apiKey;
    this.apiKeyHeaderName = options.apiKeyHeaderName ?? DEFAULT_API_KEY_HEADER;
    this.options = applyDefaults(options.initialOptions ?? {});
  }

  build() {
    return this.options;
  }

  withApiKey() {
    if (!this.apiKeyHeaderName) {
      throw new Error(
        'RequestOptions.withApiKey() does not have apiKeyHeaderName configured.',
      );
    }
    if (!this.apiKey) {
      throw new Error('RequestOptions.withApiKey() does not have apiKey configured.');
    }
    set(this.options, ['headers', this.apiKeyHeaderName], this.apiKey);
    return this;
  }

  withBody(body: string) {
    this.options.body = body;
    return this;
  }

  withHeader(name: string, value: string) {
    set(this.options, ['headers', name], value);
    return this;
  }

  withContentType(contentType: string) {
    return this.withHeader('Content-Type', contentType);
  }

  withJsonBody<T>(payload: T) {
    return this.withBody(JSON.stringify(payload)).withContentType(CONTENT_TYPE.JSON);
  }

  withMethod(method: string) {
    this.options.method = method;
    return this;
  }
}

/**
 * Provides a way to configure common request options for the various fetches that the auth
 * service perform.
 *
 * This is not meant to be used outside of this library.
 */
export interface RequestOptionsOptions {
  apiKey?: string;
  apiKeyHeader?: string;
}

export default class RequestOptions {
  private readonly apiKey: string | undefined;
  private readonly apiKeyHeaderName: string | undefined;

  constructor(options: RequestOptionsOptions = {}) {
    this.apiKey = options.apiKey;
    this.apiKeyHeaderName = options.apiKeyHeader;
  }

  options(options: RequestInit = {}) {
    return new Builder({
      apiKey: this.apiKey,
      apiKeyHeaderName: this.apiKeyHeaderName,
      initialOptions: options,
    });
  }
}
